import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGetCustomerSummariesCached } from '../hooks/cachedQueries'
import { usePostToteLabels, useGetCustomerLabelData, useSetStoreLocator } from '../hooks/backend/customersPage'
import { toast } from 'sonner'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Label } from '../lib/shadcn/label'
import { Checkbox } from '../lib/shadcn/checkbox'
import { Switch } from '../lib/shadcn/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../lib/shadcn/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { cn } from '../lib/shadcn/utils'
import { RefreshCw, Filter, Package, ShoppingCart, QrCode, Loader2, Pencil, Users, MapPin, BellOff, BellRing, Merge, History, MoreHorizontal, Building2, UserPlus } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '../lib/shadcn/tabs'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../lib/shadcn/dropdown-menu'
import { useGetCrmSettings } from '../hooks/backend/crm'
import ReorderRemindersPanel from './ui/ReorderRemindersPanel'
import MessageDialog, { type MessageTarget } from './ui/MessageDialog'
import { Badge } from '../lib/shadcn/badge'
import CustomerEditDialog from './ui/CustomerEditDialog'
import CustomerBulkEditDialog from './ui/CustomerBulkEditDialog'
import CustomerMergeDialog from './ui/CustomerMergeDialog'
import CustomerMergeHistoryDialog from './ui/CustomerMergeHistoryDialog'
import B2bSyncDialog from './ui/B2bSyncDialog'
import AddCustomerDialog from './ui/AddCustomerDialog'
import { reportStoreLocatorSync, type StoreLocatorSyncResult } from './ui/storeLocatorSync'

interface CustomerSummary {
  customer_id: string
  customer: string
  delivery_method: string
  open_orders: number
  amount: number
  items_ordered: number
  to_pick: number
  totes: number
  store_locator: boolean
  store_locator_post_id: number | null
  store_locator_error: string | null
  active: boolean
  reorder_reminders: boolean
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value || 0)
}

export default function CustomersPage() {
  const getCustomerSummaries = useGetCustomerSummariesCached()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const view = searchParams.get('tab') === 'reorders' ? 'reorders' : 'customers'
  const getCrmSettings = useGetCrmSettings()
  const [messageTarget, setMessageTarget] = useState<MessageTarget | null>(null)
  const [remindersKey, setRemindersKey] = useState(0)
  const smsConfigured = Boolean((getCrmSettings.data as { sms_configured?: boolean } | undefined)?.sms_configured)
  useEffect(() => {
    getCrmSettings.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const postToteLabels = usePostToteLabels()
  const getCustomerLabelData = useGetCustomerLabelData()
  const setStoreLocator = useSetStoreLocator()

  const [sorting, setSorting] = useState<SortingState>([])
  const [filterValue, setFilterValue] = useState('')
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeHistoryOpen, setMergeHistoryOpen] = useState(false)
  const [b2bSyncOpen, setB2bSyncOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  // Copies dialog state.
  const [dialogCustomer, setDialogCustomer] = useState<{ id: string; name: string } | null>(null)
  const [copies, setCopies] = useState('1')
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [editCustomerId, setEditCustomerId] = useState<string | null>(null)
  // Customers whose store locator sync is running (one id, or 'all').
  const [locatorBusy, setLocatorBusy] = useState<string | null>(null)

  const refreshCustomers = () => void getCustomerSummaries.trigger(undefined, { skipCache: true })

  // Turn the Web site store locator on/off for one customer, or (show
  // undefined) re-sync a list of customers as they are.
  const runStoreLocator = async (busyKey: string, customerIds: string[], show?: boolean) => {
    setLocatorBusy(busyKey)
    try {
      const result = (await setStoreLocator.trigger({
        customer_ids: customerIds,
        ...(show === undefined ? {} : { show }),
      })) as StoreLocatorSyncResult | null
      if (reportStoreLocatorSync(result) && result) {
        if (show === true) toast.success('Added to the store locator')
        else if (show === false) toast.success('Removed from the store locator')
        else toast.success(`Store locator synced (${result.synced} on the map, ${result.removed} removed)`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Store locator sync failed')
    } finally {
      setLocatorBusy(null)
      refreshCustomers()
    }
  }

  const openLabelDialog = async (customerId: string, customerName: string) => {
    setDialogCustomer({ id: customerId, name: customerName })
    setCopies('1')
    setLoadingSuggestion(true)
    try {
      const data = (await getCustomerLabelData.trigger({ customer_id: customerId })) as
        | { totes_needed?: number }
        | null
      const suggested = data?.totes_needed
      if (typeof suggested === 'number' && suggested > 0) {
        setCopies(String(suggested))
      }
    } catch {
      // Keep the default of 1 if the suggestion fails to load.
    } finally {
      setLoadingSuggestion(false)
    }
  }

  // Shipping customers send everything in one tote: print one label, no dialog.
  const [singleLabelCustomerId, setSingleLabelCustomerId] = useState<string | null>(null)
  const printSingleLabel = async (customerId: string, customerName: string) => {
    setSingleLabelCustomerId(customerId)
    try {
      await postToteLabels.trigger({ customer_id: customerId, copies: 1 })
      toast.success(`Tote label sent for ${customerName || 'customer'}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send tote label')
    } finally {
      setSingleLabelCustomerId(null)
    }
  }

  const handlePrint = async () => {
    if (!dialogCustomer) return
    const count = Math.max(1, Math.round(Number(copies) || 1))
    setPrinting(true)
    try {
      await postToteLabels.trigger({ customer_id: dialogCustomer.id, copies: count })
      toast.success(`Tote labels sent for ${dialogCustomer.name || 'customer'}`)
      setDialogCustomer(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send tote labels')
    } finally {
      setPrinting(false)
    }
  }

  useEffect(() => {
    getCustomerSummaries.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const allCustomers = useMemo<CustomerSummary[]>(
    () => (getCustomerSummaries.data as CustomerSummary[] | undefined) ?? [],
    [getCustomerSummaries.data]
  )
  const inactiveCount = useMemo(() => allCustomers.filter(c => c.active === false).length, [allCustomers])
  const customers = useMemo(
    () => (showInactive ? allCustomers : allCustomers.filter(c => c.active !== false)),
    [allCustomers, showInactive]
  )

  const stats = useMemo(
    () => ({
      customers: customers.length,
      openOrders: customers.reduce((s, c) => s + c.open_orders, 0),
      amount: customers.reduce((s, c) => s + c.amount, 0),
      toPick: customers.reduce((s, c) => s + c.to_pick, 0),
    }),
    [customers]
  )

  const columns = useMemo<ColumnDef<CustomerSummary>[]>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
            onClick={e => e.stopPropagation()}
            aria-label="Select all customers"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={value => row.toggleSelected(!!value)}
            onClick={e => e.stopPropagation()}
            aria-label={`Select ${row.original.customer || 'customer'}`}
          />
        ),
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5">
            <button
              className={cn(
                'font-medium hover:underline text-left',
                row.original.active === false ? 'text-muted-foreground' : 'text-foreground'
              )}
              title="Open customer view: orders, usual items, invoices, notes and emails"
              onClick={e => {
                e.stopPropagation()
                navigate(`/customers/${encodeURIComponent(row.original.customer_id)}`)
              }}
            >
              {row.original.customer || '—'}
            </button>
            {row.original.active === false ? (
              <Badge variant="outline" className="text-xs">Inactive</Badge>
            ) : row.original.reorder_reminders === false ? (
              <span title="Left out of reorder reminders">
                <BellOff className="w-3.5 h-3.5 text-muted-foreground" aria-label="No reorder reminders" />
              </span>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'open_orders',
        header: 'Open Orders',
        cell: ({ row }) => (
          <button
            className="inline-flex items-center gap-1 font-semibold text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
            disabled={row.original.open_orders === 0}
            onClick={e => {
              e.stopPropagation()
              navigate(`/orders?customer=${encodeURIComponent(row.original.customer)}`)
            }}
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            {row.original.open_orders}
          </button>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="text-foreground tabular-nums">{formatCurrency(row.original.amount)}</span>
        ),
      },
      {
        accessorKey: 'items_ordered',
        header: 'Items Ordered',
        cell: ({ row }) => <span className="tabular-nums">{row.original.items_ordered}</span>,
      },
      {
        accessorKey: 'to_pick',
        header: 'Needed to Pick',
        cell: ({ row }) => (
          <span
            className={cn(
              'tabular-nums font-medium',
              row.original.to_pick > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-muted-foreground'
            )}
          >
            {row.original.to_pick}
          </span>
        ),
      },
      {
        accessorKey: 'totes',
        header: 'Totes Assigned',
        cell: ({ row }) => (
          <button
            className="inline-flex items-center gap-1 font-semibold text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
            disabled={row.original.totes === 0}
            onClick={e => {
              e.stopPropagation()
              navigate(`/totes?customer_id=${encodeURIComponent(row.original.customer_id)}`)
            }}
          >
            <Package className="w-3.5 h-3.5" />
            {row.original.totes}
          </button>
        ),
      },
      {
        id: 'labels',
        header: 'Tote Labels',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.delivery_method === 'shipping' ? (
            <button
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:opacity-60 disabled:no-underline"
              disabled={singleLabelCustomerId !== null}
              onClick={e => {
                e.stopPropagation()
                void printSingleLabel(row.original.customer_id, row.original.customer)
              }}
            >
              {singleLabelCustomerId === row.original.customer_id ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <QrCode className="w-3.5 h-3.5" />
              )}
              1 label
            </button>
          ) : (
            <button
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:opacity-60 disabled:no-underline"
              onClick={e => {
                e.stopPropagation()
                void openLabelDialog(row.original.customer_id, row.original.customer)
              }}
            >
              <QrCode className="w-3.5 h-3.5" />
              Labels
            </button>
          ),
      },
      {
        id: 'edit',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            aria-label={`Edit ${row.original.customer || 'customer'}`}
            onClick={e => {
              e.stopPropagation()
              setEditCustomerId(row.original.customer_id)
            }}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, singleLabelCustomerId]
  )

  const table = useReactTable({
    data: customers,
    columns,
    state: { sorting, globalFilter: filterValue, rowSelection },
    getRowId: row => row.customer_id,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onGlobalFilterChange: setFilterValue,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const selectedCustomerIds = useMemo(
    () => Object.keys(rowSelection).filter(id => rowSelection[id]),
    [rowSelection]
  )

  // Everyone with a store on the Web site, or who should have one.
  const storeLocatorIds = useMemo(
    () => allCustomers.filter(c => c.store_locator || c.store_locator_post_id).map(c => c.customer_id),
    [allCustomers]
  )

  return (
    <div style={{ padding: '8px 12px' }} className="flex flex-col gap-4 min-h-screen bg-background overflow-y-auto">
      <Tabs value={view} onValueChange={v => setSearchParams(v === 'reorders' ? { tab: 'reorders' } : {})}>
        <TabsList>
          <TabsTrigger value="customers">
            <Users className="w-4 h-4 mr-1.5" /> All customers
          </TabsTrigger>
          <TabsTrigger value="reorders">
            <BellRing className="w-4 h-4 mr-1.5" /> Reorder reminders
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'reorders' ? (
        <div className="border rounded-lg p-4 bg-card">
          <ReorderRemindersPanel onMessage={setMessageTarget} refreshKey={remindersKey} />
        </div>
      ) : (
      <>
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border rounded-lg p-4 bg-card">
        {([
          { label: 'Customers', value: String(stats.customers) },
          { label: 'Open Orders', value: String(stats.openOrders) },
          { label: 'Open Amount', value: formatCurrency(stats.amount) },
          { label: 'Items to Pick', value: String(stats.toPick) },
        ] as const).map(s => (
          <div key={s.label} className="flex flex-col items-center p-3 rounded-md bg-background border">
            <span className="text-xs text-muted-foreground uppercase tracking-wide text-center">{s.label}</span>
            <span className="text-3xl font-bold text-foreground mt-1">{s.value}</span>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="border rounded-lg p-4 bg-card flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">Customers</h2>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <UserPlus className="w-4 h-4 mr-1" />
              Add customer
            </Button>
          </div>
          {/* Phones: filter, refresh and a "more" menu on one line. Bulk Edit
              and Merge only show once customers are selected. */}
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className={cn(selectedCustomerIds.length === 0 && 'hidden sm:inline-flex')}
              disabled={selectedCustomerIds.length === 0}
              onClick={() => setBulkEditOpen(true)}
            >
              <Users className="w-4 h-4 mr-1" />
              Bulk Edit
              {selectedCustomerIds.length > 0 ? ` (${selectedCustomerIds.length})` : ''}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={cn(selectedCustomerIds.length === 0 && 'hidden sm:inline-flex')}
              disabled={selectedCustomerIds.length !== 2}
              title="Select two customers to merge one into the other"
              onClick={() => setMergeOpen(true)}
            >
              <Merge className="w-4 h-4 mr-1" />
              Merge
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex"
              title="Merge history"
              aria-label="Merge history"
              onClick={() => setMergeHistoryOpen(true)}
            >
              <History className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex"
              disabled={storeLocatorIds.length === 0 || locatorBusy !== null}
              title="Re-send every store locator customer to poppinwtx.com"
              onClick={() => void runStoreLocator('all', storeLocatorIds)}
            >
              {locatorBusy === 'all' ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <MapPin className="w-4 h-4 mr-1" />
              )}
              Sync Store Locator ({allCustomers.filter(c => c.store_locator).length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex"
              title="Put wholesale customers and their orders on the B2B site"
              onClick={() => setB2bSyncOpen(true)}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Sync to B2B
            </Button>
            <div className="hidden sm:flex items-center gap-2 px-1">
              <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
              <Label htmlFor="show-inactive" className="text-sm font-normal whitespace-nowrap">
                Show inactive ({inactiveCount})
              </Label>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8 text-sm w-full sm:w-48"
                placeholder="Filter..."
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void getCustomerSummaries.trigger(undefined, { skipCache: true })}
              disabled={getCustomerSummaries.loading}
              aria-label="Refresh"
            >
              <RefreshCw className={cn('w-4 h-4', getCustomerSummaries.loading && 'animate-spin')} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="sm:hidden" aria-label="More actions">
                  {locatorBusy === 'all' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MoreHorizontal className="w-4 h-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem
                  checked={showInactive}
                  onCheckedChange={v => setShowInactive(v === true)}
                  onSelect={e => e.preventDefault()}
                >
                  Show inactive ({inactiveCount})
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={storeLocatorIds.length === 0 || locatorBusy !== null}
                  onSelect={() => void runStoreLocator('all', storeLocatorIds)}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Sync Store Locator ({allCustomers.filter(c => c.store_locator).length})
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setB2bSyncOpen(true)}>
                  <Building2 className="w-4 h-4 mr-2" />
                  Sync to B2B
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMergeHistoryOpen(true)}>
                  <History className="w-4 h-4 mr-2" />
                  Merge history
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(hg => (
                <TableRow key={hg.id}>
                  {hg.headers.map(header => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'whitespace-nowrap',
                        header.column.getCanSort() && 'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc'
                        ? ' ↑'
                        : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : ''}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-12">
                    {getCustomerSummaries.loading ? 'Loading…' : 'No customers found'}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map(row => (
                  <TableRow key={row.id} className="hover:bg-accent/50 transition-colors">
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs text-muted-foreground">
          {table.getFilteredRowModel().rows.length} customer(s)
        </p>
      </div>
      </>
      )}

      <MessageDialog
        target={messageTarget}
        smsConfigured={smsConfigured}
        onOpenChange={o => {
          if (!o) {
            setMessageTarget(null)
            setRemindersKey(k => k + 1)
          }
        }}
      />

      <Dialog
        open={dialogCustomer !== null}
        onOpenChange={open => {
          if (!open && !printing) setDialogCustomer(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Print Tote Labels</DialogTitle>
            <DialogDescription>
              {dialogCustomer?.name || 'Customer'} — enter the number of label copies to print.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="copies">Number of copies</Label>
            <Input
              id="copies"
              type="number"
              min={1}
              value={copies}
              disabled={loadingSuggestion}
              onChange={e => setCopies(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {loadingSuggestion
                ? 'Loading suggested number of totes…'
                : 'Defaulted to the suggested number of totes for the open order size.'}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogCustomer(null)}
              disabled={printing}
            >
              Cancel
            </Button>
            <Button onClick={() => void handlePrint()} disabled={printing || loadingSuggestion}>
              {printing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Printing…
                </>
              ) : (
                'Print'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerEditDialog
        customerId={editCustomerId}
        onOpenChange={open => { if (!open) setEditCustomerId(null) }}
        onSaved={refreshCustomers}
      />

      <CustomerBulkEditDialog
        open={bulkEditOpen}
        customerIds={selectedCustomerIds}
        onOpenChange={setBulkEditOpen}
        onSaved={() => {
          setRowSelection({})
          void getCustomerSummaries.trigger(undefined, { skipCache: true })
        }}
      />

      <CustomerMergeDialog
        open={mergeOpen}
        customerIds={selectedCustomerIds}
        onOpenChange={setMergeOpen}
        onMerged={() => {
          setRowSelection({})
          refreshCustomers()
        }}
      />

      <AddCustomerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={refreshCustomers}
      />
      <B2bSyncDialog
        open={b2bSyncOpen}
        onOpenChange={setB2bSyncOpen}
        onSynced={() => void getCustomerSummaries.trigger(undefined, { skipCache: true })}
      />
      <CustomerMergeHistoryDialog
        open={mergeHistoryOpen}
        onOpenChange={setMergeHistoryOpen}
        onUndone={refreshCustomers}
      />
    </div>
  )
}
